// ResultsManager.js - Version finale avec adaptation mobile et labels explicites
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  CheckCircle, Clock, AlertCircle, TrendingUp, Calendar, Target, Award, 
  ChevronRight, ChevronDown, X, Brain, HelpCircle, 
  Zap, Trophy, Eye, EyeOff, Filter, Download, Maximize, Minimize
} from 'lucide-react';

const ResultsManager = ({ 
  actionPlan,
  currentStepId,
  competenceData,
  userObjective,
  userProgress,
  getCurrentStep,
  getStepNotes,
  onUpdateStepResults,
  onStepComplete,
  onStepChange,
  weeklyPlanning,
  conversationHistory
}) => {
  // États locaux
  const [expandedSteps, setExpandedSteps] = useState([currentStepId]);
  const [selectedStepForView, setSelectedStepForView] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [showDashboard, setShowDashboard] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Synchroniser les étapes expandées avec l'étape courante
  useEffect(() => {
    if (currentStepId && !expandedSteps.includes(currentStepId)) {
      setExpandedSteps(prev => [...prev, currentStepId]);
    }
  }, [currentStepId, expandedSteps]);

  // Gérer le mode plein écran RÉEL
  const toggleFullscreen = useCallback(() => {
    const elem = document.getElementById('results-manager-container');
    
    if (!document.fullscreenElement) {
      if (elem?.requestFullscreen) {
        elem.requestFullscreen().then(() => {
          setIsFullscreen(true);
        }).catch(err => {
          console.error('Erreur plein écran:', err);
        });
      }
    } else {
      document.exitFullscreen().then(() => {
        setIsFullscreen(false);
      }).catch(err => {
        console.error('Erreur sortie plein écran:', err);
      });
    }
  }, []);

  // Écouter les changements de plein écran
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Nettoyer le texte des notes - VERSION AMÉLIORÉE pour garder uniquement la dernière entrée
  const cleanNoteText = useCallback((text) => {
    if (!text) return '';
    
    const entries = text.split('\n\n').filter(section => section.trim());
    if (entries.length === 0) return '';
    
    const lastEntry = entries[entries.length - 1];
    const lines = lastEntry.split('\n');
    const cleanedLines = lines.filter(line => {
      const trimmedLine = line.trim();
      
      if ((trimmedLine.includes('[Notes]') || trimmedLine.includes('[Synthèse')) && 
          /\d{2}\/\d{2}\/\d{4}/.test(trimmedLine)) {
        return false;
      }
      
      if (/^[✅☑️✓\s\[\]]+\d{2}\/\d{2}\/\d{4}/.test(trimmedLine)) {
        return false;
      }
      
      return trimmedLine.length > 0;
    });
    
    return cleanedLines.join('\n').trim();
  }, []);

  // Calcul des métriques de progression basées sur les notes
  const calculateStepProgress = useCallback((step) => {
    let stepNotes = {};
    
    if (competenceData?.stepNotes && competenceData.stepNotes[step.id]) {
      stepNotes = competenceData.stepNotes[step.id];
    } else if (getStepNotes) {
      stepNotes = getStepNotes(step.id) || {};
    }
    
    const metrics = {
      hasDecisions: false,
      hasQuestions: false,
      hasRecommendations: false,
      decisionsCount: 0,
      questionsCount: 0,
      recommendationsCount: 0,
      completionScore: 0,
      lastActivity: null,
      activityLevel: 'none'
    };

    let totalSections = 0;

    if (stepNotes.decisions && stepNotes.decisions.trim().length > 0) {
      metrics.hasDecisions = true;
      const sections = stepNotes.decisions.split('\n\n').filter(s => s.trim());
      metrics.decisionsCount = sections.length;
      totalSections++;
    }

    if (stepNotes.questions && stepNotes.questions.trim().length > 0) {
      metrics.hasQuestions = true;
      const sections = stepNotes.questions.split('\n\n').filter(s => s.trim());
      metrics.questionsCount = sections.length;
      totalSections++;
    }

    if (stepNotes.ai_recommendations && stepNotes.ai_recommendations.trim().length > 0) {
      metrics.hasRecommendations = true;
      const sections = stepNotes.ai_recommendations.split('\n\n').filter(s => s.trim());
      metrics.recommendationsCount = sections.length;
      totalSections++;
    }

    if (step.termine) {
      metrics.completionScore = 100;
    } else {
      let score = 0;
      if (metrics.hasDecisions) score += 40;
      if (metrics.hasQuestions) score += 30;
      if (metrics.hasRecommendations) score += 30;
      metrics.completionScore = Math.min(score, 95);
    }

    if (stepNotes.updatedAt) {
      metrics.lastActivity = new Date(stepNotes.updatedAt);
    }

    if (totalSections === 0) {
      metrics.activityLevel = 'none';
    } else if (totalSections === 1) {
      metrics.activityLevel = 'low';
    } else if (totalSections === 2) {
      metrics.activityLevel = 'medium';
    } else {
      metrics.activityLevel = 'high';
    }

    return metrics;
  }, [getStepNotes, competenceData]);

  // Statistiques globales du plan d'action
  const planStats = useMemo(() => {
    if (!actionPlan?.steps) {
      return {
        totalSteps: 0,
        completedSteps: 0,
        stepsInProgress: 0,
        stepsNotStarted: 0,
        overallProgress: 0,
        totalActions: 0,
        completedActions: 0,
        todayActions: 0,
        totalDecisions: 0,
        calculatedPoints: 0
      };
    }

    const stats = {
      totalSteps: actionPlan.steps.length,
      completedSteps: 0,
      stepsInProgress: 0,
      stepsNotStarted: 0,
      overallProgress: 0,
      totalActions: 0,
      completedActions: 0,
      todayActions: 0,
      totalDecisions: 0,
      calculatedPoints: 0
    };

    let totalProgress = 0;

    actionPlan.steps.forEach(step => {
      const metrics = calculateStepProgress(step);
      
      if (step.termine) {
        stats.completedSteps++;
        stats.completedActions++;
        stats.calculatedPoints += 100;
      } else {
        if (metrics.activityLevel !== 'none') {
          stats.stepsInProgress++;
        } else {
          stats.stepsNotStarted++;
        }
        stats.calculatedPoints += Math.round(metrics.completionScore * 0.95);
      }
      
      stats.totalDecisions += metrics.decisionsCount;
      totalProgress += metrics.completionScore;
    });

    stats.overallProgress = Math.round(totalProgress / stats.totalSteps);

    if (weeklyPlanning && Array.isArray(weeklyPlanning)) {
      const today = new Date().toISOString().split('T')[0];
      
      weeklyPlanning.forEach(week => {
        if (week.elementaryActions && Array.isArray(week.elementaryActions)) {
          week.elementaryActions.forEach(action => {
            stats.totalActions++;
            if (action.completed) {
              stats.completedActions++;
            }
            if (action.date === today) {
              stats.todayActions++;
            }
          });
        }
      });
    }

    return stats;
  }, [actionPlan, weeklyPlanning, calculateStepProgress]);

  // Filtrer et trier les étapes - étape courante en premier
  const filteredSteps = useMemo(() => {
    if (!actionPlan?.steps) return [];
    
    const filtered = actionPlan.steps.filter(step => {
      const metrics = calculateStepProgress(step);
      
      switch(filterStatus) {
        case 'completed':
          return step.termine;
        case 'active':
          return !step.termine && metrics.activityLevel !== 'none';
        case 'notStarted':
          return !step.termine && metrics.activityLevel === 'none';
        default:
          return true;
      }
    });

    // Trier : étape courante en premier, puis les autres dans l'ordre original
    return filtered.sort((a, b) => {
      if (a.id === currentStepId) return -1;
      if (b.id === currentStepId) return 1;
      return 0;
    });
  }, [actionPlan, filterStatus, calculateStepProgress, currentStepId]);

  // Basculer l'expansion d'une étape
  const toggleStepExpansion = (stepId) => {
    setExpandedSteps(prev => 
      prev.includes(stepId) 
        ? prev.filter(id => id !== stepId)
        : [...prev, stepId]
    );
  };

  // Formater la date relative
  const formatRelativeTime = (date) => {
    if (!date) return 'Jamais';
    
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'À l\'instant';
    if (diffMins < 60) return `Il y a ${diffMins} min`;
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    if (diffDays < 7) return `Il y a ${diffDays}j`;
    
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  // Exporter les données
  const handleExport = () => {
    const exportData = {
      objective: userObjective,
      generatedAt: new Date().toISOString(),
      stats: planStats,
      steps: actionPlan?.steps?.map(step => {
        const metrics = calculateStepProgress(step);
        let stepNotes = {};
        
        if (competenceData?.stepNotes && competenceData.stepNotes[step.id]) {
          stepNotes = competenceData.stepNotes[step.id];
        } else if (getStepNotes) {
          stepNotes = getStepNotes(step.id) || {};
        }
        
        return {
          title: step.titre,
          description: step.description,
          completed: step.termine,
          metrics,
          notes: {
            decisions: cleanNoteText(stepNotes.decisions || ''),
            questions: cleanNoteText(stepNotes.questions || ''),
            recommendations: cleanNoteText(stepNotes.ai_recommendations || '')
          }
        };
      }) || []
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `progression-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Composant Dashboard avec indicateurs cliquables - ADAPTÉ MOBILE
  const ProgressDashboard = () => {
    const handleCardClick = (cardType) => {
      switch(cardType) {
        case 'progress':
          setFilterStatus('all');
          break;
        case 'actions':
          setFilterStatus('completed');
          break;
        case 'today':
          setFilterStatus('active');
          break;
        case 'points':
          setFilterStatus('all');
          break;
      }
    };

    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3 mb-4">
        <button
          onClick={() => handleCardClick('progress')}
          className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-2 md:p-3 border border-blue-200 hover:shadow-md transition-all text-left"
        >
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-medium text-blue-700">Progression</span>
            <TrendingUp className="h-3 w-3 md:h-4 md:w-4 text-blue-600" />
          </div>
          <div className="text-xl md:text-2xl font-bold text-blue-900 mb-0.5">
            {planStats.overallProgress}%
          </div>
          <div className="text-xs text-blue-600">
            {planStats.completedSteps}/{planStats.totalSteps} étapes
          </div>
        </button>

        <button
          onClick={() => handleCardClick('actions')}
          className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-2 md:p-3 border border-green-200 hover:shadow-md transition-all text-left"
        >
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-medium text-green-700">Actions</span>
            <CheckCircle className="h-3 w-3 md:h-4 md:w-4 text-green-600" />
          </div>
          <div className="text-xl md:text-2xl font-bold text-green-900 mb-0.5">
            {planStats.completedActions}/{planStats.totalActions}
          </div>
          <div className="text-xs text-green-600">
            Complétées
          </div>
        </button>

        <button
          onClick={() => handleCardClick('today')}
          className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-2 md:p-3 border border-purple-200 hover:shadow-md transition-all text-left"
        >
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-medium text-purple-700">Décisions</span>
            <Calendar className="h-3 w-3 md:h-4 md:w-4 text-purple-600" />
          </div>
          <div className="text-xl md:text-2xl font-bold text-purple-900 mb-0.5">
            {planStats.totalDecisions}
          </div> 
          <div className="text-xs text-purple-600">
            Décisions prises
          </div>
        </button>

        <button
          onClick={() => handleCardClick('points')}
          className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-2 md:p-3 border border-orange-200 hover:shadow-md transition-all text-left"
        >
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-medium text-orange-700">Points</span>
            <Trophy className="h-3 w-3 md:h-4 md:w-4 text-orange-600" />
          </div>
          <div className="text-xl md:text-2xl font-bold text-orange-900 mb-0.5">
            {planStats.calculatedPoints}
          </div>
          <div className="text-xs text-orange-600">
            Niveau {Math.floor(planStats.calculatedPoints / 100) + 1}
          </div>
        </button>
      </div>
    );
  };

  // Composant Carte d'étape avec système de badges - ADAPTÉ MOBILE avec labels explicites
  const StepCard = ({ step, isExpanded, isCurrent }) => {
    const metrics = calculateStepProgress(step);
    const [activeNoteSection, setActiveNoteSection] = useState(null);
    
    const activityLevelLabels = {
      none: 'Non démarrée',
      low: 'Début',
      medium: 'En cours',
      high: 'Très active'
    };

    const activityLevelColors = {
      none: 'text-gray-500 bg-gray-100',
      low: 'text-yellow-700 bg-yellow-100',
      medium: 'text-blue-700 bg-blue-100',
      high: 'text-green-700 bg-green-100'
    };

    const getStepNotesData = () => {
      if (competenceData?.stepNotes && competenceData.stepNotes[step.id]) {
        return competenceData.stepNotes[step.id];
      } else if (getStepNotes) {
        return getStepNotes(step.id) || {};
      }
      return {};
    };

    const stepNotes = getStepNotesData();

    return (
      <div className={`border rounded-lg transition-all ${
        isCurrent 
          ? 'border-blue-400 bg-blue-50 shadow-md' 
          : step.termine 
            ? 'border-green-300 bg-green-50' 
            : 'border-gray-200 bg-white hover:shadow-sm'
      }`}>
        <div 
          className="p-3 md:p-4 cursor-pointer"
          onClick={() => toggleStepExpansion(step.id)}
        >
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                {step.termine ? (
                  <CheckCircle className="h-4 w-4 md:h-5 md:w-5 text-green-600 flex-shrink-0" />
                ) : (
                  <Clock className={`h-4 w-4 md:h-5 md:w-5 flex-shrink-0 ${
                    metrics.activityLevel === 'none' ? 'text-gray-400' : 'text-blue-600'
                  }`} />
                )}
                <h3 className={`font-semibold text-base md:text-lg ${
                  step.termine ? 'text-green-800' : 'text-gray-900'
                }`}>
                  {step.titre}
                </h3>
                {isCurrent && (
                  <span className="px-2 py-0.5 bg-blue-600 text-white text-xs rounded-full whitespace-nowrap">
                    Actuelle
                  </span>
                )}
              </div>

              {step.description && (
                <p className="text-xs md:text-sm text-gray-600 mb-3 line-clamp-2">
                  {step.description}
                </p>
              )}

              <div className="flex items-center gap-1.5 md:gap-2 flex-wrap">
                <span className={`text-xs px-2 py-1 rounded-full ${activityLevelColors[metrics.activityLevel]}`}>
                  {activityLevelLabels[metrics.activityLevel]}
                </span>
                
                {/* Badge Décisions avec label explicite */}
                {metrics.hasDecisions && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveNoteSection(activeNoteSection === 'decisions' ? null : 'decisions');
                    }}
                    className={`flex items-center gap-1 text-xs px-2 py-1 rounded-full transition-all whitespace-nowrap ${
                      activeNoteSection === 'decisions'
                        ? 'bg-green-600 text-white shadow-md'
                        : 'bg-green-100 text-green-700 hover:bg-green-200'
                    }`}
                  >
                    <CheckCircle className="h-3 w-3" />
                    <span>{metrics.decisionsCount} {metrics.decisionsCount > 1 ? 'Décisions' : 'Décision'}</span>
                  </button>
                )}
                
                {/* Badge Questions avec label explicite */}
                {metrics.hasQuestions && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveNoteSection(activeNoteSection === 'questions' ? null : 'questions');
                    }}
                    className={`flex items-center gap-1 text-xs px-2 py-1 rounded-full transition-all whitespace-nowrap ${
                      activeNoteSection === 'questions'
                        ? 'bg-orange-600 text-white shadow-md'
                        : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                    }`}
                  >
                    <HelpCircle className="h-3 w-3" />
                    <span>{metrics.questionsCount} {metrics.questionsCount > 1 ? 'Questions' : 'Question'}</span>
                  </button>
                )}

                {/* Badge Recommandations avec label explicite */}
                {metrics.hasRecommendations && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveNoteSection(activeNoteSection === 'recommendations' ? null : 'recommendations');
                    }}
                    className={`flex items-center gap-1 text-xs px-2 py-1 rounded-full transition-all whitespace-nowrap ${
                      activeNoteSection === 'recommendations'
                        ? 'bg-purple-600 text-white shadow-md'
                        : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                    }`}
                  >
                    <Brain className="h-3 w-3" />
                    <span>{metrics.recommendationsCount} {metrics.recommendationsCount > 1 ? 'recommandations' : 'recommandation'}</span>
                  </button>
                )}

                {metrics.lastActivity && (
                  <div className="flex items-center gap-1 text-xs text-gray-500 ml-auto">
                    <Clock className="h-3 w-3" />
                    <span className="hidden sm:inline">{formatRelativeTime(metrics.lastActivity)}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2 md:gap-3 flex-shrink-0">
              <div className="text-center">
                <div className={`text-xl md:text-2xl font-bold ${
                  step.termine ? 'text-green-600' : 'text-blue-600'
                }`}>
                  {metrics.completionScore}%
                </div>
              </div>
              
              {isExpanded ? (
                <ChevronDown className="h-4 w-4 md:h-5 md:w-5 text-gray-400" />
              ) : (
                <ChevronRight className="h-4 w-4 md:h-5 md:w-5 text-gray-400" />
              )}
            </div>
          </div>

          <div className="mt-3">
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className={`h-2 rounded-full transition-all ${
                  step.termine ? 'bg-green-500' : 'bg-blue-500'
                }`}
                style={{ width: `${metrics.completionScore}%` }}
              />
            </div>
          </div>
        </div>

        {isExpanded && (
          <div className="px-3 md:px-4 pb-3 md:pb-4 space-y-3 border-t border-gray-100 pt-3 md:pt-4">
            {activeNoteSection === 'decisions' && metrics.hasDecisions && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 md:p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 md:h-5 md:w-5 text-green-600" />
                    <span className="text-sm font-semibold text-green-800">
                      Décisions & actions
                    </span>
                  </div>
                  <button
                    onClick={() => setActiveNoteSection(null)}
                    className="text-green-600 hover:text-green-800"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
                <div className="text-sm text-green-900 whitespace-pre-wrap leading-relaxed">
                  {cleanNoteText(stepNotes.decisions)}
                </div>
              </div>
            )}

            {activeNoteSection === 'questions' && metrics.hasQuestions && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 md:p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <HelpCircle className="h-4 w-4 md:h-5 md:w-5 text-orange-600" />
                    <span className="text-sm font-semibold text-orange-800">
                      Questions & défis
                    </span>
                  </div>
                  <button
                    onClick={() => setActiveNoteSection(null)}
                    className="text-orange-600 hover:text-orange-800"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
                <div className="text-sm text-orange-900 whitespace-pre-wrap leading-relaxed">
                  {cleanNoteText(stepNotes.questions)}
                </div>
              </div>
            )}

            {activeNoteSection === 'recommendations' && metrics.hasRecommendations && (
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 md:p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Brain className="h-4 w-4 md:h-5 md:w-5 text-purple-600" />
                    <span className="text-sm font-semibold text-purple-800">
                      Recommandations
                    </span>
                  </div>
                  <button
                    onClick={() => setActiveNoteSection(null)}
                    className="text-purple-600 hover:text-purple-800"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
                <div className="text-sm text-purple-900 whitespace-pre-wrap leading-relaxed">
                  {cleanNoteText(stepNotes.ai_recommendations)}
                </div>
              </div>
            )}

            {!isCurrent && !step.termine && (
              <div className="pt-2 border-t border-gray-100">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onStepChange?.(step.id);
                  }}
                  className="w-full px-2 py-1.5 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-1.5"
                >
                  <Target className="h-3 w-3" />
                  Définir comme courante
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div 
      id="results-manager-container"
      className={`h-full flex flex-col ${isFullscreen ? 'fixed inset-0 z-50 bg-gray-50' : 'bg-gray-50'}`}
    >
      {/* En-tête adapté mobile */}
      <div className="bg-white border-b border-gray-200 p-2 md:p-4">
        <div className="flex items-center justify-between mb-3 md:mb-4 gap-2">
          <div className="flex-1 min-w-0">
            <h2 className="text-lg md:text-2xl font-bold text-gray-900 truncate">Suivi de progression</h2>
            <p className="text-xs md:text-sm text-gray-600 mt-1 hidden md:block">
              Basé sur vos notes d'avancement et décisions
            </p>
          </div>
          
          <div className="flex items-center gap-1 md:gap-2 flex-shrink-0">
            <button
              onClick={() => setShowDashboard(!showDashboard)}
              className="px-2 py-2 md:px-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-1 md:gap-2"
              title={showDashboard ? "Masquer les indicateurs" : "Afficher les indicateurs"}
            >
              {showDashboard ? (
                <>
                  <EyeOff className="h-4 w-4" />
                  <span className="hidden lg:inline text-sm">Masquer</span>
                </>
              ) : (
                <>
                  <Eye className="h-4 w-4" />
                  <span className="hidden lg:inline text-sm">Indicateurs</span>
                </>
              )}
            </button>

            <button
              onClick={toggleFullscreen}
              className="px-2 py-2 md:px-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-1 md:gap-2"
title={isFullscreen ? "Quitter le plein écran" : "Plein écran"}
            >
              {isFullscreen ? (
                <>
                  <Minimize className="h-4 w-4" />
                  <span className="hidden lg:inline text-sm">Quitter</span>
                </>
              ) : (
                <>
                  <Maximize className="h-4 w-4" />
                  <span className="hidden lg:inline text-sm">Plein écran</span>
                </>
              )}
            </button>

            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-2 py-2 md:px-3 border border-gray-300 rounded-lg text-xs md:text-sm"
            >
              <option value="all">Toutes</option>
              <option value="active">En cours</option>
              <option value="completed">Terminées</option>
              <option value="notStarted">Non démarrées</option>
            </select>

            <button
              onClick={handleExport}
              className="hidden md:flex px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors items-center gap-2"
            >
              <Download className="h-4 w-4" />
              <span className="hidden lg:inline text-sm">Exporter</span>
            </button>
          </div>
        </div>

        {showDashboard && <ProgressDashboard />}
      </div>

      {/* Liste des étapes - adaptée mobile */}
      <div className="flex-1 overflow-auto p-2 md:p-4">
        <div className="max-w-5xl mx-auto space-y-3 md:space-y-4">
          {filteredSteps.length > 0 ? (
            filteredSteps.map(step => (
              <StepCard
                key={step.id}
                step={step}
                isExpanded={expandedSteps.includes(step.id)}
                isCurrent={step.id === currentStepId}
              />
            ))
          ) : (
            <div className="text-center py-12">
              <AlertCircle className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Aucune étape à afficher
              </h3>
              <p className="text-gray-600">
                {filterStatus !== 'all' 
                  ? 'Essayez de modifier les filtres' 
                  : 'Commencez par créer un plan d\'action'}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Modal détails - adaptée mobile */}
      {selectedStepForView && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 md:p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-3 md:p-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg md:text-xl font-bold text-gray-900 pr-4">
                  {selectedStepForView.titre}
                </h3>
                <button
                  onClick={() => setSelectedStepForView(null)}
                  className="text-gray-400 hover:text-gray-600 flex-shrink-0"
                >
                  <X className="h-5 w-5 md:h-6 md:w-6" />
                </button>
              </div>
            </div>

            <div className="p-4 md:p-6">
              <div className="prose max-w-none">
                <p className="text-sm md:text-base text-gray-600 mb-6">{selectedStepForView.description}</p>
                
                {(() => {
                  const metrics = calculateStepProgress(selectedStepForView);
                  let stepNotes = {};
                  
                  if (competenceData?.stepNotes && competenceData.stepNotes[selectedStepForView.id]) {
                    stepNotes = competenceData.stepNotes[selectedStepForView.id];
                  } else if (getStepNotes) {
                    stepNotes = getStepNotes(selectedStepForView.id) || {};
                  }
                  
                  return (
                    <div className="space-y-4 md:space-y-6">
                      {stepNotes.decisions && (
                        <div>
                          <h4 className="text-base md:text-lg font-semibold text-green-800 mb-3 flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 md:h-5 md:w-5" />
                            Décisions & actions
                          </h4>
                          <div className="whitespace-pre-wrap text-sm text-gray-700 bg-green-50 p-3 md:p-4 rounded-lg">
                            {cleanNoteText(stepNotes.decisions)}
                          </div>
                        </div>
                      )}

                      {stepNotes.questions && (
                        <div>
                          <h4 className="text-base md:text-lg font-semibold text-orange-800 mb-3 flex items-center gap-2">
                            <HelpCircle className="h-4 w-4 md:h-5 md:w-5" />
                            Questions & défis
                          </h4>
                          <div className="whitespace-pre-wrap text-sm text-gray-700 bg-orange-50 p-3 md:p-4 rounded-lg">
                            {cleanNoteText(stepNotes.questions)}
                          </div>
                        </div>
                      )}

                      {stepNotes.ai_recommendations && (
                        <div>
                          <h4 className="text-base md:text-lg font-semibold text-purple-800 mb-3 flex items-center gap-2">
                            <Brain className="h-4 w-4 md:h-5 md:w-5" />
                            Recommandations
                          </h4>
                          <div className="whitespace-pre-wrap text-sm text-gray-700 bg-purple-50 p-3 md:p-4 rounded-lg">
                            {cleanNoteText(stepNotes.ai_recommendations)}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResultsManager;